import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Star, MapPin, Search, ArrowLeft, Loader2, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClinicMap } from "@/components/ClinicMap";
import { SiteFooter } from "@/components/SiteFooter";
import api from "@/lib/api";
import { isAuVisitor, auParams } from "@/lib/auMode";

const haversine = (a, b) => {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)));
};

export default function ClinicDirectory() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [locFilter, setLocFilter] = useState("All");
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    api.get("/clinics", { params: auParams() }).then((r) => setClinics(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const auMode = isAuVisitor();

  const nearMe = () => {
    if (!navigator.geolocation) { toast.error("Location isn't supported here"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Sorted by distance from you"); },
      (err) => { setLocating(false); toast.error(err.code === err.PERMISSION_DENIED ? "Location permission denied" : "Couldn't get your location"); },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const categories = useMemo(() => {
    const set = new Set();
    clinics.forEach((c) => c.categories.forEach((x) => set.add(x)));
    return ["All", ...Array.from(set)];
  }, [clinics]);

  const locations = useMemo(() => ["All", ...Array.from(new Set(clinics.map((c) => c.location)))], [clinics]);

  const filtered = useMemo(() => {
    let list = clinics.filter((c) => {
      const text = `${c.name} ${c.location} ${c.specialties.join(" ")}`.toLowerCase();
      if (q && !text.includes(q.toLowerCase())) return false;
      if (cat !== "All" && !c.categories.includes(cat)) return false;
      if (locFilter !== "All" && c.location !== locFilter) return false;
      return true;
    });
    if (userPos) {
      list = list
        .map((c) => ({ ...c, distance: c.lat != null ? haversine(userPos, { lat: c.lat, lng: c.lng }) : Infinity }))
        .sort((a, b) => a.distance - b.distance);
    }
    return list;
  }, [clinics, q, cat, locFilter, userPos]);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl">AuraLift</span>
          </Link>
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-brand-slate hover:text-brand-primary" data-testid="directory-home-btn">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl mb-2">Find a clinic</h1>
            <p className="text-brand-slate">Browse verified partner clinics by treatment and location.</p>
          </div>
          <Button data-testid="near-me-btn" onClick={nearMe} disabled={locating} variant="outline" className="rounded-full border-brand-primary/40 text-brand-primary hover:bg-brand-primary/5 gap-2">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />} Clinics near me
          </Button>
        </div>

        <ClinicMap clinics={filtered} userPos={userPos} onSelect={(slug) => navigate(`/clinic/${slug}`)} className="mb-8" />

        {auMode && (
          <div data-testid="au-directory-note" className="mb-8 rounded-2xl border border-brand-accent/30 bg-brand-accent/5 p-5 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-brand-accent shrink-0 mt-0.5" />
            <p className="text-sm text-brand-slate leading-relaxed">
              You appear to be in Australia. In line with TGA and AHPRA rules, treatments involving prescription-only medicines and patient reviews are not shown online. Book a consultation to discuss options with a registered practitioner.
            </p>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-brand-border/60 p-5 mb-8 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 text-brand-slate absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="clinic-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city or specialty…"
              className="pl-10 rounded-full h-11"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${cat === c ? "bg-brand-primary text-white" : "bg-brand-sand text-brand-slate hover:bg-brand-sand/70"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-brand-slate flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location:</span>
            {locations.map((l) => (
              <button
                key={l}
                data-testid={`filter-loc-${l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                onClick={() => setLocFilter(l)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${locFilter === l ? "bg-brand-accent text-white" : "bg-brand-sand text-brand-slate hover:bg-brand-sand/70"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand-primary animate-spin" /></div>
        ) : (
          <>
            <p className="text-sm text-brand-slate mb-4" data-testid="results-count">{filtered.length} clinic{filtered.length !== 1 ? "s" : ""}{userPos ? " · nearest first" : ""}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-list">
              {filtered.map((c, i) => (
                <motion.div key={c.slug} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}>
                  <Link to={`/clinic/${c.slug}`} data-testid={`directory-card-${c.slug}`} className="block h-full p-6 rounded-2xl bg-white border border-brand-border/60 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-heading text-lg leading-tight">{c.name}</h3>
                      <span className="flex items-center gap-1 text-sm shrink-0 ml-2"><Star className="w-3.5 h-3.5 text-brand-accent fill-brand-accent" /> {c.rating}</span>
                    </div>
                    <p className="text-xs text-brand-slate flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> {c.location}</p>
                    {userPos && c.distance !== Infinity && (
                      <p className="text-xs text-brand-primary font-medium mb-2" data-testid={`distance-${c.slug}`}>{c.distance.toLocaleString()} km away</p>
                    )}
                    <p className="text-sm text-brand-slate mb-3">{c.tagline}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {c.categories.map((cat2) => (
                        <span key={cat2} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-sand text-brand-primary">{cat2}</span>
                      ))}
                    </div>
                    <span className="text-sm text-brand-primary font-medium mt-3 inline-block">View treatments →</span>
                  </Link>
                </motion.div>
              ))}
            </div>
            {filtered.length === 0 && <p className="text-center text-brand-slate py-16">No clinics match your filters.</p>}
          </>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}

